import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MessageSquare, RefreshCw } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Stat({ label, value, testid }) {
  return (
    <div className="pf-card" data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-display text-2xl text-slate-900 mt-1">{value ?? "—"}</p>
    </div>
  );
}

function Flags({ flags, mode }) {
  if (!flags) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="admin-sms-flags">
      {Object.entries(flags).map(([k, v]) => (
        <span key={k} className={`text-xs px-2 py-1 rounded-full ${v ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
          {k}={String(v)}
        </span>
      ))}
      <span className="text-xs px-2 py-1 rounded-full bg-cyan-100 text-cyan-700" data-testid="admin-sms-provider-mode">provider: {mode}</span>
    </div>
  );
}

export default function AdminSmsConsole() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [audience, setAudience] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setError(null);
    try {
      const q = new URLSearchParams({ limit: "50", ...(category && { category }), ...(audience && { audience }) });
      const [s, m] = await Promise.all([api.get("/admin/sms/summary"), api.get(`/admin/sms/messages?${q}`)]);
      setSummary(s.data); setItems(m.data.items);
    } catch { setError("Could not load SMS activity."); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [category, audience]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-sms-console" className="min-w-0 max-w-[calc(100vw-3rem)] md:max-w-none">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="pf-chip pf-chip-cyan"><MessageSquare className="w-3 h-3" /> SMS</div>
          <h2 className="font-display text-3xl font-medium tracking-tight mt-3 text-slate-900">SMS operations</h2>
          <p className="text-sm text-slate-600 mt-1">Canada-only. Numbers are masked; Twilio credentials are never shown.</p>
        </div>
        <button type="button" onClick={refresh} className="pf-btn text-sm flex items-center gap-1" data-testid="admin-sms-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <Flags flags={summary?.flags} mode={summary?.provider_mode} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Sent" value={summary?.sent} testid="admin-sms-sent" />
        <Stat label="Delivered" value={summary?.delivered} testid="admin-sms-delivered" />
        <Stat label="Failed" value={summary?.failed} testid="admin-sms-failed" />
        <Stat label="Blocked" value={summary?.blocked} testid="admin-sms-blocked" />
      </div>
      <div className="flex gap-2 mt-6">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" data-testid="admin-sms-filter-category" aria-label="Category">
          <option value="">All categories</option><option>SECURITY</option><option>TRANSACTIONAL</option><option>MARKETING</option>
        </select>
        <select value={audience} onChange={(e) => setAudience(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" data-testid="admin-sms-filter-audience" aria-label="Audience">
          <option value="">Merchants &amp; shoppers</option><option value="MERCHANT">Merchants</option><option value="SHOPPER">Shoppers</option>
        </select>
      </div>
      {loading && <p className="text-sm text-slate-500 mt-3" data-testid="admin-sms-loading">Loading SMS activity…</p>}
      {error && <p className="text-sm text-rose-600 mt-3">{error}</p>}
      <div className="pf-card mt-3 overflow-x-auto max-w-full">
        <table className="w-full text-sm" data-testid="admin-sms-table">
          <thead><tr className="text-left text-xs text-slate-500">
            <th className="py-2 pr-3">Time</th><th className="pr-3">Audience</th><th className="pr-3">Category</th>
            <th className="pr-3">Template</th><th className="pr-3">Recipient</th><th className="pr-3">Country</th>
            <th className="pr-3">Status</th><th>Reason</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="py-4 text-slate-500" data-testid="admin-sms-empty">No SMS activity yet.</td></tr>}
            {items.map((m) => (
              <tr key={m.id} className="border-t border-slate-100" data-testid={`admin-sms-row-${m.id}`}>
                <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(m.created_at)}</td>
                <td className="pr-3">{m.audience || "—"}</td><td className="pr-3">{m.category || "—"}</td>
                <td className="pr-3">{m.template}</td><td className="pr-3 font-mono">{m.recipient}</td>
                <td className="pr-3">{m.country}</td><td className="pr-3">{m.status}</td>
                <td className="text-xs text-slate-500">{m.block_reason || m.failure_reason || m.failure_code || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
